import { Box, Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import {
  Dialog,
  DialogButtons,
  DialogContent,
  DialogMessageBox,
  DialogTitle,
} from '@verantos-dev/shared-components';

import { useAdProcessModal } from '../hooks/useAdProcessModal';
import type { AdProcessModalProps } from '../types';

const DIALOG_PAPER_SX = { width: 600, maxHeight: '80%', margin: 0 } as const;

const Bold = ({ children }: { children: React.ReactNode }) => (
  <strong style={{ fontWeight: 700 }}>{children}</strong>
);

export const AdProcessModal = ({ onClose }: AdProcessModalProps) => {
  const {
    title,
    errorTitle,
    errorText,
    retryButtonLabel,
    isSubmitting,
    submittingError,
    onRetry,
    confirmTitle,
    confirmMessage1,
    fromStatusLabel,
    processingLabel,
    processedLabel,
    confirmButtonLabel,
    onConfirm,
    onCancel,
    isSuccess,
    successTitle,
    successText,
    successCountdownSeconds,
    onSuccessComplete,
  } = useAdProcessModal({ onClose });

  // Three views: confirm -> success -> error. There is no separate "processing"
  // view — the run stays on the confirm view while in flight and the confirm
  // button switches to a disabled spinner (mirrors cohort's process modal).
  // On success the modal holds open on a countdown, then navigates to the
  // executed version. Error restores the close affordance so the user can give up.
  const inError = !!submittingError && !isSubmitting;
  const inConfirm = !isSuccess && !inError;

  const dialogTitle = inConfirm ? confirmTitle : title;

  // The close ("X") affordance maps to the active view:
  //   success   → onSuccessComplete (skip the countdown and navigate now)
  //   error     → onClose (let the user give up)
  //   in flight → none, so there is no exit affordance (the BE write would
  //               still land but the UI would lose the success/navigation handoff)
  //   confirm   → onCancel
  let handleClose: (() => void) | undefined;
  if (isSuccess) {
    handleClose = onSuccessComplete;
  } else if (inError) {
    handleClose = onClose;
  } else if (isSubmitting) {
    handleClose = undefined;
  } else {
    handleClose = onCancel;
  }

  return (
    <Dialog
      id="adProcessModal"
      open
      PaperProps={{ sx: DIALOG_PAPER_SX }}
      fullWidth
      maxWidth="lg"
      onClose={handleClose}
    >
      <DialogTitle id="ad-process-modal-title" showClose={!!handleClose} onClose={handleClose}>
        {dialogTitle}
      </DialogTitle>
      <DialogContent>
        {inConfirm ? (
          <DialogMessageBox
            id="ad-process-modal-confirm"
            title={confirmMessage1}
            type="info"
            text={
              <Stack gap={2}>
                <div>
                  <FormattedMessage
                    id="analyticalDatasetDetail.processModal.message2"
                    values={{
                      fromStatus: <Bold>{fromStatusLabel}</Bold>,
                      processing: <Bold>{processingLabel}</Bold>,
                    }}
                  />
                </div>
                <div>
                  <FormattedMessage
                    id="analyticalDatasetDetail.processModal.message3"
                    values={{ processed: <Bold>{processedLabel}</Bold> }}
                  />
                </div>
              </Stack>
            }
          />
        ) : inError ? (
          <DialogMessageBox
            id="ad-process-modal-error"
            title={errorTitle}
            type="error"
            text={errorText}
          />
        ) : isSuccess ? (
          <Stack gap={2}>
            <DialogMessageBox
              id="ad-process-modal-success"
              title={successTitle}
              type="success"
              text={successText}
            />
            <Box>
              <FormattedMessage
                id="analyticalDatasetDetail.processModal.successCountdown"
                values={{ count: successCountdownSeconds }}
              />
            </Box>
          </Stack>
        ) : null}
      </DialogContent>
      {inConfirm && (
        <DialogButtons
          id="ad-process-modal-buttons"
          saveButton={
            isSubmitting
              ? // In flight: keep the confirm copy, swap the button to a disabled
                // spinner so the user waits here instead of on a separate modal.
                {
                  label: processingLabel,
                  onClick: () => undefined,
                  color: 'primary',
                  disabled: true,
                  wait: true,
                }
              : { label: confirmButtonLabel, onClick: onConfirm, color: 'primary' }
          }
        />
      )}
      {inError && (
        <DialogButtons
          id="ad-process-modal-buttons"
          saveButton={{ label: retryButtonLabel, onClick: onRetry }}
        />
      )}
    </Dialog>
  );
};
